import {
  getDecimals,
  isNumericField,
} from '../features/recommendations/Table-widgets/TableData/RecommendationsTableData';

const isDateColumn = (columnId: string) => {
  const result =
    columnId.toLowerCase().includes('date') ||
    columnId.toLowerCase().includes('created') ||
    ['datestring', 'beforedatestring', 'afterdatestring'].includes(columnId.toLowerCase());
  return result;
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
};

const formatNumberForFilter = (value: any, columnId: string, useFixedDecimals = false) => {
  if (value === null || value === undefined || value === '') return '';

  const numValue = Number(value);
  if (isNaN(numValue)) return String(value);

  return useFixedDecimals ? numValue.toFixed(2) : numValue.toFixed(getDecimals(columnId));
};

export const createMultiConditionFilter = (
  setCurrentFilters: (filters: any[]) => void,
  currentFiltersRef: React.RefObject<any[]>,
  useFixedDecimals = false,
  rawData?: any,
  customIsNumericField?: (columnId: string) => boolean,
) => {
  return (rows: any, columnIds: any, filterValue: any) => {
    if (!filterValue || !filterValue.conditions || !filterValue.logicOperator) {
      return rows;
    }

    const { conditions, logicOperator } = filterValue;

    const filteredRows = rows.filter((row: any) => {
      let cellValue: any;
      if (rawData && Array.isArray(rawData)) {
        const wellValue = row.original?.Well || row.values?.Well;
        const rawRow = rawData.find((rawItem: any) => rawItem?.Well?.value === wellValue);
        cellValue =
          rawRow?.[columnIds[0]]?.value ||
          row.original?.[columnIds[0]] ||
          row.values?.[columnIds[0]] ||
          row[columnIds[0]];
      } else {
        cellValue = row.original
          ? row.original[columnIds[0]]
          : row.values
            ? row.values[columnIds[0]]
            : row[columnIds[0]];
      }

      const columnId = columnIds[0];
      const dateColumn = isDateColumn(columnId);

      const compare = (condition: any) => {
        let value = cellValue;
        let filterVal = condition.value;

        if (dateColumn) {
          value = formatDate(cellValue);
          filterVal = formatDate(filterVal);
        } else if (customIsNumericField ? customIsNumericField(columnId) : isNumericField(columnId)) {
          value = formatNumberForFilter(cellValue, columnId, useFixedDecimals);
          filterVal = formatNumberForFilter(filterVal, columnId, useFixedDecimals);
        } else {
          value = value == null ? '' : String(value);
          filterVal = filterVal == null ? '' : String(filterVal);
        }

        switch (condition.operator) {
          case 'is equal to':
          case 'Is equal to':
            return value?.toLowerCase() === filterVal?.toLowerCase();
          case 'is not equal to':
          case 'Is not equal to':
            return value?.toLowerCase() !== filterVal?.toLowerCase();
          case 'Starts with':
            return value?.toLowerCase().startsWith?.(filterVal.toLowerCase());
          case 'Contains':
            return value?.toLowerCase().includes?.(filterVal.toLowerCase());
          case 'Does not contain':
            return !value?.toLowerCase().includes?.(filterVal.toLowerCase());
          case 'Ends with':
            return value?.toLowerCase().endsWith?.(filterVal.toLowerCase());
          case 'is empty':
            return value === '' || value === null || value === undefined;
          case 'is not empty':
            return value !== '' && value !== null && value !== undefined;
          case 'Is after or equal to':
            return new Date(cellValue) >= new Date(filterVal);
          case 'Is after':
            return new Date(cellValue) > new Date(filterVal);
          case 'Is before or equal to':
            return new Date(cellValue) <= new Date(filterVal);
          case 'Is before':
            return new Date(cellValue) < new Date(filterVal);
          case 'Is null':
            return !cellValue || cellValue === '';
          case 'Is not null':
            return !!cellValue && cellValue !== '';
          default:
            return false;
        }
      };

      if (logicOperator === 'And') {
        return conditions.every(compare);
      } else if (logicOperator === 'Or') {
        return conditions.some(compare);
      }
      return true;
    });

    // Update the currentFiltersRef directly
    const filtered = currentFiltersRef.current
      ? currentFiltersRef.current.filter((filter) => filter.columnId !== columnIds[0])
      : [];
    filtered.push({ columnId: columnIds[0], filterValue });
    if (currentFiltersRef.current) {
      currentFiltersRef.current.length = 0;
      filtered.forEach((f) => currentFiltersRef.current!.push(f));
    }
    setCurrentFilters(currentFiltersRef.current ? [...currentFiltersRef.current] : []);
    return filteredRows;
  };
};
